import React from "react";
import { FileCopyLine, InformationLine } from "@ui/IconV2/react";
import { colors } from "@ui/Theme";
import Text from "@ui/Text";
import t from "../../../i18n";
import { styled } from "@linaria/react";
import Button from "@ui/Button";
import useToasts from "@ui/Toast/useToasts";
import TooltipV2 from "@ui/TooltipV2";
import { css } from "@linaria/core";
import { useReportingErrors } from "./useReportingErrors";
import type { Reporting } from "../../../api/useReporting";

const TooltipContent = styled.div`
  max-width: 420px;
`;

const TooltipIconContainer = styled.div`
  height: 20px;
`;

const StyledFileCopyLine = styled(FileCopyLine)`
  max-width: 14px;
  max-height: 14px;
`;

const TooltipButton = css`
  height: auto;
`;

function ReportingErrorIndicator({ reporting }: { reporting: Reporting }) {
  const reportingErrors = useReportingErrors(reporting);
  const { addToast } = useToasts();

  const hasReportingErrors = React.useMemo(
    () =>
      reportingErrors.projectsWithoutPublishedAnalysesCount > 0 ||
      reportingErrors.projectsWithManyPublishedAnalysesCount > 0,
    [reportingErrors],
  );

  const copyReportingErrorLog = React.useCallback(() => {
    if (!reportingErrors.projectsWithManyPublishedAnalysesLog) return;
    window.navigator.clipboard
      .writeText(reportingErrors.projectsWithManyPublishedAnalysesLog)
      .then(() => addToast("success", t("commons.copyDetail.toast")))
      .catch(() => addToast("error", t("errors.unexpected_error")));
  }, [addToast, reportingErrors.projectsWithManyPublishedAnalysesLog]);

  if (!hasReportingErrors) return null;

  return (
    <TooltipV2
      placement="bottom"
      overlay={
        <TooltipContent>
          {reportingErrors.projectsWithoutPublishedAnalysesCount > 0 && (
            <>
              <Text type="body2">
                {t("reporting.errors.no_published_analyses", {
                  count: reportingErrors.projectsWithoutPublishedAnalysesCount,
                })}
              </Text>
              <br />
            </>
          )}
          {reportingErrors.projectsWithManyPublishedAnalysesCount > 0 && (
            <>
              <Text type="body2">
                {t("reporting.errors.multi_published_analyses", {
                  count: reportingErrors.projectsWithManyPublishedAnalysesCount,
                })}
              </Text>
              <br />
              {reportingErrors.projectsWithManyPublishedAnalysesLog && (
                <Button
                  type="button"
                  variant="link"
                  onClick={copyReportingErrorLog}
                  leftIcon={<StyledFileCopyLine />}
                  className={TooltipButton}
                >
                  <Text type="body2">{t("reporting.errors.copy_details")}</Text>
                </Button>
              )}
            </>
          )}
        </TooltipContent>
      }
    >
      <TooltipIconContainer>
        <InformationLine size="m" color={colors.carbon800} />
      </TooltipIconContainer>
    </TooltipV2>
  );
}

export default React.memo(ReportingErrorIndicator);
